import { cn } from '@/lib/utils/cn';

export type BadgeVariant =
  | 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'accent'
  // Aliases used throughout the app:
  | 'primary' | 'secondary' | 'default';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  dot?: boolean;
}

const variantStyles: Record<BadgeVariant, string> = {
  success: 'bg-[color-mix(in_srgb,var(--color-success)_15%,transparent)] text-[var(--color-success)] border-[color-mix(in_srgb,var(--color-success)_30%,transparent)]',
  warning: 'bg-[color-mix(in_srgb,var(--color-warning)_15%,transparent)] text-[var(--color-warning)] border-[color-mix(in_srgb,var(--color-warning)_30%,transparent)]',
  error:   'bg-[color-mix(in_srgb,var(--color-error)_15%,transparent)] text-[var(--color-error)] border-[color-mix(in_srgb,var(--color-error)_30%,transparent)]',
  info:    'bg-[color-mix(in_srgb,var(--color-accent-secondary)_15%,transparent)] text-[var(--color-accent-secondary)] border-[color-mix(in_srgb,var(--color-accent-secondary)_30%,transparent)]',
  neutral: 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
  accent:  'bg-[color-mix(in_srgb,var(--color-accent-primary)_15%,transparent)] text-[var(--color-accent-primary)] border-[color-mix(in_srgb,var(--color-accent-primary)_30%,transparent)]',
  primary: 'bg-[color-mix(in_srgb,var(--color-accent-primary)_15%,transparent)] text-[var(--color-accent-primary)] border-[color-mix(in_srgb,var(--color-accent-primary)_30%,transparent)]',
  secondary: 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
  default: 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
};

const dotStyles: Record<BadgeVariant, string> = {
  success: 'bg-[var(--color-success)]',
  warning: 'bg-[var(--color-warning)]',
  error:   'bg-[var(--color-error)]',
  info:    'bg-[var(--color-accent-secondary)]',
  neutral: 'bg-[var(--color-text-secondary)]',
  accent:  'bg-[var(--color-accent-primary)]',
  primary: 'bg-[var(--color-accent-primary)]',
  secondary: 'bg-[var(--color-text-secondary)]',
  default: 'bg-[var(--color-text-secondary)]',
};

export function Badge({
  variant = 'neutral',
  size = 'md',
  dot = false,
  className,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 border rounded-full font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs',
        variantStyles[variant],
        className,
      )}
      {...props}
    >
      {dot && (
        <span
          className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', dotStyles[variant])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
